import { countries } from '@/consts/languageAccordance';
import { HOW_HEARD_PHRASES, PROFESSIONS_PHRASES } from '@/consts/phrases/dictionaryPhrases';
import { addOpacityToThemeColor } from '@/utils/styles/addOpacity';
import { TypographyVariantsOptions } from '@mui/material/styles/createTypography';
import { IPageLayout } from 'types/types';

type Shades = '10' | '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';

type ShadesRecord = Partial<Record<Shades, string>>;
type Palette = {
    primary: ShadesRecord & { main: string; contrastText: string };
    secondary: ShadesRecord & { main: string };
    tertiary: ShadesRecord & { main: string };
    neutral: ShadesRecord;
    background: { default: string };
    text: { primary: string };
    success: { main: string };
    info: { main: string };
    warning: { main: string };
    error: { main: string };
};

export const colors = {
    white: 'rgba(255, 255, 255, 1)',
    black: 'rgba(0, 0, 0, 1)',
    lightGray: 'rgba(210, 210, 210, 1)',
    gray: 'rgba(140, 140, 140, 1)',
    primary: {
        '50': 'rgba(238, 246, 248, 1)',
        '100': 'rgba(220, 238, 241, 1)',
        '200': 'rgba(186, 220, 227, 1)',
        '300': 'rgba(151, 203, 212, 1)',
        '400': 'rgba(117, 185, 198, 1)',
        '500': 'rgba(82, 168, 184, 1)',
        '600': 'rgba(66, 134, 147, 1)',
        '700': 'rgba(49, 101, 110, 1)',
        '800': 'rgba(33, 67, 74, 1)',
        '900': 'rgba(16, 34, 37, 1)',
    },
    secondary: {
        '50': 'rgba(198, 189, 223, 1)',
        '100': 'rgba(183, 173, 210, 1)',
        '200': 'rgba(152, 140, 184, 1)',
        '300': 'rgba(121, 107, 157, 1)',
        '400': 'rgba(90, 74, 131, 1)',
        '500': 'rgba(59, 41, 105, 1)',
        '600': 'rgba(47, 33, 84, 1)',
        '700': 'rgba(35, 25, 63, 1)',
        '800': 'rgba(24, 16, 42, 1)',
        '900': 'rgba(12, 8, 21, 1)',
    },
    tertiary: {
        '50': 'rgba(255, 247, 230, 1)',
        '100': 'rgba(254, 239, 204, 1)',
        '200': 'rgba(253, 223, 153, 1)',
        '300': 'rgba(252, 208, 102, 1)',
        '400': 'rgba(251, 192, 51, 1)',
        '500': 'rgba(250, 176, 0, 1)',
        '600': 'rgba(200, 141, 0, 1)',
        '700': 'rgba(150, 106, 0, 1)',
        '800': 'rgba(100, 70, 0, 1)',
        '900': 'rgba(50, 35, 0, 1)',
    },
    neutral: {
        '10': 'rgba(250, 250, 250, 1)',
        '50': 'rgba(245, 246, 247, 1)',
        '100': 'rgba(240, 240, 240, 1)',
        '200': 'rgba(225, 225, 225, 1)',
        '300': 'rgba(210, 210, 210, 1)',
        '400': 'rgba(181, 181, 181, 1)',
        '500': 'rgba(140, 140, 140, 1)',
        '600': 'rgba(120, 120, 120, 1)',
        '700': 'rgba(92, 94, 97, 1)',
        '800': 'rgba(59, 59, 59, 1)',
        '900': 'rgba(13, 13, 13, 1)',
    },
    success: 'rgba(82, 184, 158, 1)',
    info: 'rgba(82, 113, 184, 1)',
    warning: 'rgba(250, 176, 0, 1)',
    error: 'rgba(217, 56, 33, 1)',
};

/**
 * The sortOrder object contains arrays for sorting various elements.
 * The '*' character is a wildcard that sorts remaining elements at the end or start.
 *
 * - STRING ARRAY: Used with `sortByArray` in utils/sort.ts.
 *
 * - MULTIDIMENSIONAL ARRAY (with phraseId's):
 *   - Outer array: Sort order
 *   - Inner array: Sorted by the phraseId's translation
 *   - Used with `sortObjectsByTranslations` in utils/sort.ts.
 */
const sortOrder = {
    locale: [
        'en-INT',
        'en-AU',
        'nl-BE',
        'fr-BE',
        'pt-BR',
        'en-CA',
        'fr-CA',
        'da-DK',
        'de-DE',
        'es-ES',
        'fr-FR',
        'en-IN',
        'it-IT',
        'es-MX',
        'nl-NL',
        'de-NL',
        'en-NZ',
        'no-NO',
        'de-AT',
        'en-AT',
        'pt-PT',
        'de-CH',
        'fr-CH',
        'it-CH',
        'fi-FI',
        'sv-SE',
        'en-GB',
        'en-US',
    ],
    howHeardSelectOptions: [
        ['*'],
        [String(HOW_HEARD_PHRASES.OTHER_SOCIAL_MEDIA)],
        [String(HOW_HEARD_PHRASES.OTHER)],
    ],
    professionalSelectOptions: [
        [String(PROFESSIONS_PHRASES.PHYSICIAN_POST_TRAINING)],
        [String(PROFESSIONS_PHRASES.PHYSICIAN_IN_TRAINING_RESIDENT_FELLOW)],
        [String(PROFESSIONS_PHRASES.AYUSH_DOCTOR_PRACTIONER)],
        [String(PROFESSIONS_PHRASES.PHYSICIAN_ASSISTANT)],
        [String(PROFESSIONS_PHRASES.NURSING_PROFESSIONAL)],
        ['divider'],
        ['*'],
    ],
    countriesSelectOptions: [
        [String(countries['ca'].phraseId)],
        [String(countries['us'].phraseId)],
        ['*'],
    ],
};

const palette: Palette = {
    primary: {
        ...colors.primary,
        main: colors.primary[500],
        contrastText: colors.white,
    },
    secondary: {
        ...colors.secondary,
        main: colors.secondary[500],
    },
    tertiary: {
        ...colors.tertiary,
        main: colors.tertiary[500],
    },
    background: {
        default: colors.neutral[10],
    },
    text: {
        primary: colors.neutral[800],
    },
    neutral: colors.neutral,
    success: {
        main: colors.success,
    },
    info: {
        main: colors.info,
    },
    warning: {
        main: colors.warning,
    },
    error: {
        main: colors.error,
    },
};

const fonts = {
    primary: "'Poppins', serif",
    secondary: "'Poppins', serif",
    additional: "'Poppins', serif",
    body: {
        fontSize: '1rem',
    },
};

export const variables = {
    themeName: 'mpanels',
    sortOrder,
    colors: {
        palette,
        links: {
            main: colors.primary[500],
            hover: colors.primary[300],
            active: colors.primary[700],
            disabled: colors.neutral[500],
        },
        error: {
            main: palette.error.main,
        },
        other: {
            badge: {
                topMenu: {
                    color: colors.white,
                    background: palette.primary.main,
                },
            },
            chip: {
                default: {
                    background: colors.neutral[50],
                    text: palette.secondary[300],
                },
                methodologyType: {
                    telephone: {
                        background: colors.neutral[50],
                        text: colors.secondary[200],
                    },
                    online: {
                        background: colors.neutral[50],
                        text: colors.secondary[400],
                    },
                    inPerson: {
                        background: colors.neutral[50],
                        text: colors.secondary[300],
                    },
                },
            },
            table: {
                evenRowHighlight: colors.neutral['50'],
                hoverHighlight: colors.neutral['100'],
                headerText: colors.neutral['500'],
                text: palette.text.primary,
                hoverText: palette.text.primary,
            },
            background: {
                header: addOpacityToThemeColor(colors.white, 0.9),
                topMenu: colors.white,
                footer: colors.neutral[100],
                highlight: addOpacityToThemeColor(palette.primary.main, 0.06),
                highlightHover: colors.primary['50'],
                success: addOpacityToThemeColor(colors.success, 0.1),
                warning: addOpacityToThemeColor(colors.warning, 0.1),
                info: addOpacityToThemeColor(colors.info, 0.1),
                error: addOpacityToThemeColor(colors.error, 0.1),
                divideline: addOpacityToThemeColor(colors.black, 0.16),
                preloader: palette.primary.main,
            },
            text: {
                header: palette.text.primary,
                registrationDescription: 'rgba(50, 50, 50)',
                footer: colors.neutral[700],
                copyrightText: colors.neutral[700],
                lightGray: 'rgba(218, 218, 218)',
                gray: colors.gray,
                darkGray: colors.gray,
                success: colors.success,
                warning: colors.warning,
                info: colors.info,
                error: colors.error,
            },
            inputs: {
                default: colors.neutral['500'],
                hover: colors.primary['300'],
                focus: colors.primary['500'],
            },
            icon: {
                stroke: {
                    primary: palette.secondary.main,
                    secondary: palette.secondary.main,
                    tertiary: palette.secondary.main,
                },
                background: {
                    primary: 'transparent',
                    secondary: 'transparent',
                    tertiary: 'transparent',
                },
            },
            notifications: {
                success: colors.success,
                info: colors.info,
                warning: colors.warning,
                error: colors.error,
            },
        },
    },
    fonts,
    borderRadius: {
        primary: '0',
        secondary: '0',
        tertiary: '0',
        topMenu: '0',
        mobileTopMenu: '0',
    },
    padding: {
        inputsDefault: '8.5px 12px',
        tableCellPadding: '8px 16px',
        elementPadding: '0 0 12px 0',
        cardPadding: '28px 32px',
    },
    margin: {
        elementMargin: '0 0 12px 0',
        hrMargin: '30px 0',
    },
    boxShadows: {
        popoverShadow: `0px 1px 2px ${addOpacityToThemeColor(palette.primary.main, 0.16)}`,
        tableShadow: `0px 1px 2px ${addOpacityToThemeColor(palette.primary.main, 0.16)}`,
        tableSelectedShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
        headerShadow: 'initial',
        buttonShadow: 'initial',
        inputShadow: `0px 0px 0px 2px ${addOpacityToThemeColor(palette.primary.main, 0.5)}`,
        tooltipShadow: `0px 4px 4px 0px ${addOpacityToThemeColor(palette.text.primary, 0.16)}`,
        descriptiveShadow: 'initial',
    },
    layout: {
        helpCentreInsteadFaq: true,
        home: {
            hasBackgroundImage: 'centerTop',
            hasWelcomeText: true,
            pageTitle: {
                textAlign: 'left',
                position: 'inline',
            },
            description: {
                visible: true,
                position: 'top',
            },
            picBlock: {
                visible: false,
                position: 'top',
                picWidth: 390,
                picHeight: 280,
            },
            iconsBlock: {
                visible: true,
                position: 'bottom',
                inlineIcons: true,
                paperBox: true,
            },
            registrationForm: {
                fullwidthForm: false,
                showStepTitle: false,
                stepperOrientation: 'horizontal',
            },
        },
        footer: {
            fullWidth: true,
            logoPosition: 'left',
            themeColorSocialIcons: true,
            companyLinkVisible: false,
            inlineFollowUs: false,
            hasFollowUsTitle: true,
        },
    } as IPageLayout,
    cookie: {
        button: {
            hover: {
                color: 'white',
                backgroundColor: colors.primary['300'],
            },
            borderRadius: '0',
            shadow: 'none',
        },
        toggle: {
            initial: {
                selected: {
                    toggleColor: colors.primary['500'],
                    backgroundColor: addOpacityToThemeColor(colors.primary['500'], 0.5),
                },
                unselected: {
                    toggleColor: 'white',
                    backgroundColor: addOpacityToThemeColor(colors.neutral['500'], 0.38),
                },
            },
            disabled: {
                toggleColor: colors.neutral['100'],
                backgroundColor: colors.neutral['300'],
            },
        },
    },
    typography: {
        fontFamily: fonts.primary,
        h1: {
            color: palette.secondary.main,
            fontSize: '2.375rem', // 38px
            fontWeight: '600',
        },
        h2: {
            color: palette.secondary.main,
            fontSize: '2rem', // 32px
            fontWeight: '600',
        },
        h3: {
            color: palette.text.primary,
            fontSize: '1.5rem', // 24px
            fontWeight: '600',
        },
        h4: {
            color: palette.text.primary,
            fontSize: '1.125rem', // 18px
            fontWeight: '600',
        },
        h5: {
            color: palette.text.primary,
            fontSize: '1rem', // 16px
            fontWeight: '600',
        },
        h6: {
            color: palette.text.primary,
            fontSize: '0.875rem', // 14px
            fontWeight: '600',
        },
        body1: {
            color: palette.text.primary,
            fontSize: '1rem', // 16px
        },
        body2: {
            color: palette.text.primary,
            fontSize: '0.875rem', // 14px
        },
        subtitle1: {
            color: palette.text.primary,
            fontSize: '1rem', // 16px
        },
        subtitle2: {
            color: palette.text.primary,
            fontSize: '0.875rem', // 14px
        },
        subtitle3: {
            color: palette.text.primary,
            fontSize: '0.75rem', // 12px
        },
        greyLabel: {
            fontSize: '0.75rem', // 12px
        },
        link: {
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'inherit',
            },
            '&:active': {
                textDecoration: 'inherit',
            },
            '&:disabled': {
                textDecoration: 'inherit',
            },
        },
    } as TypographyVariantsOptions,
};

export type ThemeVariables = typeof variables;

// Add this type declaration to extend TypographyVariantsOptions
declare module '@mui/material/styles/createTypography' {
    interface TypographyVariantsOptions {
        link: {
            textDecoration: string;
            '&:hover'?: React.CSSProperties;
            '&:active'?: React.CSSProperties;
            '&:disabled'?: React.CSSProperties;
        };
    }
}
